/**
 * Module API — the safe surface exposed to module handler code.
 *
 * Handlers receive a frozen ModuleAPI object that provides read access
 * to game state and limited write access (chat messages, event emitting).
 * This is the contract between Machina and third-party module code.
 */
/** Maximum delay allowed in module handlers (5 seconds) */
const MAX_DELAY_MS = 5000;
/** Current SDK version — bump on every public release */
export const SDK_VERSION = '0.3.0';
/** Build a frozen ModuleAPI from host providers */
export function createModuleAPI(providers) {
    const api = {
        moduleId: providers.moduleId,
        campaignId: providers.campaignId,
        playset: providers.playset,
        systemId: providers.systemId,
        sdkVersion: SDK_VERSION,
        getBestiary: () => providers.getBestiary(),
        getCreature: (name) => providers.getCreature(name),
        isInCombat: () => providers.isInCombat(),
        getCombatants: () => providers.getCombatants(),
        getCurrentTurn: () => providers.getCurrentTurn(),
        getRound: () => providers.getRound(),
        getCharacters: () => providers.getCharacters(),
        getCharacter: (name) => providers.getCharacter(name),
        getActiveScene: () => providers.getActiveScene(),
        sendSystemMessage: (text) => providers.sendSystemMessage(text),
        sendNpcMessage: (npcName, text) => providers.sendNpcMessage(npcName, text),
        emit: (event, payload) => providers.emit(event, payload),
        roll: (expression) => providers.roll(expression),
        rollDetailed: (expression) => providers.rollDetailed(expression),
        // Tokens
        getTokens: () => providers.getTokens(),
        getToken: (name) => providers.getToken(name),
        addToken: (params) => providers.addToken(params),
        removeToken: (id) => providers.removeToken(id),
        moveToken: (id, position) => providers.moveToken(id, position),
        // Sprites
        generateSprite: (params) => providers.generateSprite(params),
        setSprite: (name, imageUrl, facing) => providers.setSprite(name, imageUrl, facing),
        getSpriteCount: (name) => providers.getSpriteCount(name),
        setSpriteSeed: (name, imageUrl) => providers.setSpriteSeed(name, imageUrl),
        getSpriteSeed: (name) => providers.getSpriteSeed(name),
        random: (min, max) => {
            const lo = Math.ceil(min);
            const hi = Math.floor(max);
            return Math.floor(Math.random() * (hi - lo + 1)) + lo;
        },
        delay: (ms) => {
            const capped = Math.min(Math.max(0, ms), MAX_DELAY_MS);
            return new Promise((resolve) => setTimeout(resolve, capped));
        },
        getStorage: (key) => providers.getStorage(key),
        setStorage: (key, value) => providers.setStorage(key, value),
        log: (message) => console.log(`[${providers.moduleName}] ${message}`),
    };
    return Object.freeze(api);
}
//# sourceMappingURL=module-api.js.map